import React from 'react';

/**
 * MascotPicture — la mascotte servie en WebP, PNG en repli.
 *
 * Le PNG source pèse 1,56 Mo (1014×1153) ; le WebP 520 px en fait 57,9 Ko,
 * soit 27× moins. Partout où le repo affiche la mascotte via un
 * `<picture>` (BureauV2, MonJuriste, HomeV2, PlansV2, TeamPage), c'est le
 * WebP qui part sur le réseau et le PNG ne sert que de repli. Quatre
 * points ne l'avaient pas : ils téléchargeaient le PNG entier. Ce
 * composant leur donne le MÊME pattern, sans le réécrire quatre fois.
 *
 * Le balisage est celui déjà en place dans le repo, à l'identique :
 *   <picture><source type="image/webp" srcSet={webp} /><img src={png} /></picture>
 *
 * `display: contents` sur le <picture> : le wrapper ne génère aucune
 * boîte, donc l'<img> reste l'enfant direct de son conteneur, avec sa
 * classe et ses styles. Sans cela, envelopper changerait la mise en page
 * là où l'image est un bloc centré (`.lc2-empty-mascot`, margin auto) ou
 * un item flex (`.lc2-row-mascot`, flex: none) — le <picture>, inline et
 * sans dimensions propres, s'interposerait. La sélection de la source
 * WebP, elle, ne dépend pas du `display` : elle est faite par l'<img>
 * d'après son parent <picture>. Passer `wrapStyle` permet de reprendre
 * la main si un appelant a besoin d'une boîte.
 */

// Résolution tolérante — la garde déjà en place partout dans le repo :
// si l'asset n'est pas au build, `require` throw et on rend null plutôt
// que de casser la page.
const assetUrl = (m) => (m && m.default) || m || null;
let PNG = null;
try {
  // eslint-disable-next-line global-require
  PNG = assetUrl(require('../../assets/mascotte-archer.png'));
} catch (_) { PNG = null; }
let WEBP = null;
try {
  // eslint-disable-next-line global-require
  WEBP = assetUrl(require('../../assets/mascotte-archer-520.webp'));
} catch (_) { WEBP = null; }

/** Sources résolues, pour les appelants qui s'en servent de garde. */
export const MASCOT_PNG = PNG;
export const MASCOT_WEBP = WEBP;

/** Dimensions intrinsèques du PNG source, pour les rendus fluides. */
export const MASCOT_INTRINSIC = { width: 1014, height: 1153 };

/**
 * @param {number} width   largeur explicite du <img> (anti-layout-shift).
 * @param {number} height  hauteur explicite du <img>.
 * @param {string} alt     vide par défaut → l'image est décorative et
 *                         reçoit aria-hidden, comme avant. Un alt non
 *                         vide la repasse en contenu.
 * @param {string} loading 'lazy' UNIQUEMENT hors du premier écran. Au
 *                         -dessus de la ligne de flottaison, ne rien
 *                         passer : l'image doit rester prioritaire.
 */
export default function MascotPicture({
  className,
  style,
  wrapStyle,
  alt = '',
  width = MASCOT_INTRINSIC.width,
  height = MASCOT_INTRINSIC.height,
  loading,
  decoding,
  ...rest
}) {
  if (!PNG) return null;
  return (
    <picture style={{ display: 'contents', ...wrapStyle }}>
      {/* `display: none` explicite : aucune feuille de style UA ne masque
          <source>, il ne disparaît d'ordinaire que parce que le <picture>
          ne rend pas ses enfants non-img. Sous `display: contents` il
          redevient un enfant du conteneur — mesuré : un item flex de
          largeur nulle qui consomme un `gap` entier et décalait l'image
          de 10 px dans les bulles du chat, de 14 px dans le bloc IA. */}
      {WEBP && <source srcSet={WEBP} type="image/webp" style={{ display: 'none' }} />}
      <img
        src={PNG}
        alt={alt}
        aria-hidden={alt ? undefined : 'true'}
        className={className}
        style={style}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        {...rest}
      />
    </picture>
  );
}
